import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { CompanyContext } from './CompanyContext'
import { useSocialNetworks } from 'utils/hooks/socials/useSocialNetworks'
import useLazyEffect from 'utils/hooks/useLazyEffect'
import { api } from 'api/http'

/**
 * Thin compatibility shim. Conversation polling continues to run on a 30s
 * interval and `Api.put` is replaced with the new axios client. Aggregation
 * across socials still goes through `useSocialNetworks` until that hook is
 * migrated.
 */

const ConversationContext = React.createContext({
    conversations: [],
    isLoadingConversations: false,
    updateConversationFields: () => {},
    updateConversations: () => {},
    updateLastReadAt: () => {},
})

const initialStateFilters = {
    query: '',
    assignedTo: false,
    unread: false,
}

const sortConversations = (list) =>
    [...list]
        .sort(
            (a, b) =>
                new Date(b?.lastMessage?.created_at) -
                new Date(a?.lastMessage?.created_at)
        )
        .sort(
            (a, b) =>
                b?.unreadMessages_count - a?.unreadMessages_count
        )

const ConversationProvider = ({ children }) => {
    const { company, companySocials } = useContext(CompanyContext)
    const { getConversations } = useSocialNetworks()
    const [conversations, setConversations] = useState()
    const [isLoadingConversations, setIsLoadingConversations] =
        useState(false)
    const [filters, setFilters] = useState(initialStateFilters)

    const setSortedConversations = useCallback((list) => {
        setConversations(sortConversations(list || []))
    }, [])

    const updateConversationFields = useCallback(
        (external_id, obj) => {
            let _newConversations
            setConversations((prev) => {
                _newConversations = (prev || []).map((x) =>
                    x.id === external_id ? { ...x, ...obj } : x
                )
                return sortConversations(_newConversations)
            })
            return _newConversations
        },
        []
    )

    const updateConversations = useCallback(() => {
        if (
            company &&
            companySocials?.length &&
            company?.id == companySocials?.[0]?.company_id
        ) {
            setIsLoadingConversations(true)
            getConversations(filters)
                .then((next) => {
                    setSortedConversations(next || [])
                })
                .finally(() => {
                    setIsLoadingConversations(false)
                })
        } else {
            setSortedConversations([])
        }
    }, [
        company,
        companySocials,
        filters,
        getConversations,
        setSortedConversations,
    ])

    const updateLastReadAt = useCallback(
        (_conversation) => {
            const _new = updateConversationFields(_conversation?.id, {
                unreadMessages_count: 0,
            })
            api.put(
                `/conversations/${_conversation?.id}`,
                {},
                { update_last_read_at: true }
            ).catch(() => {})
            return _new
        },
        [updateConversationFields]
    )

    useEffect(() => {
        updateConversations()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companySocials?.[0]?.id])

    useEffect(() => {
        const interval = setInterval(() => {
            updateConversations()
        }, 30 * 1000)
        return () => clearInterval(interval)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companySocials?.[0]?.id, filters])

    useEffect(() => {
        setConversations(undefined)
        updateConversations()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.unread, filters.assignedTo])

    useLazyEffect(
        () => {
            setConversations(undefined)
            updateConversations()
        },
        [filters.query],
        400
    )

    const value = useMemo(
        () => ({
            conversations,
            isLoadingConversations,
            updateConversationFields,
            updateConversations,
            updateLastReadAt,
            filters,
            setFilters,
        }),
        [
            conversations,
            isLoadingConversations,
            updateConversationFields,
            updateConversations,
            updateLastReadAt,
            filters,
        ]
    )

    return (
        <ConversationContext.Provider value={value}>
            {children}
        </ConversationContext.Provider>
    )
}

export { ConversationContext, ConversationProvider }
